import { z } from "zod";

export const paymentProviderTypeSchema = z
  .enum(["stripe"])
  .describe("PaymentProviderType");

// Whether the account can calculate automatic tax at checkout; the two
// requires* members name which setup step is missing.
export const automaticTaxStatusSchema = z
  .enum(["available", "requiresPaymentAccount", "requiresTaxSetup"])
  .describe("AutomaticTaxStatus");

export type AutomaticTaxStatus = z.infer<typeof automaticTaxStatusSchema>;

/**
 * Where a project stands on being able to charge shoppers, as one total answer
 * so every surface switches once instead of re-walking the account fields.
 * `inReview` and `rejected` are the two the merchant cannot act on: the
 * provider is verifying details it already has, or it has declined the account.
 */
export const paymentAccountStatusSchema = z
  .enum([
    "notConnected",
    "onboardingIncomplete",
    "inReview",
    "actionRequired",
    "rejected",
    "ready",
  ])
  .describe("PaymentAccountStatus");

export type PaymentAccountStatus = z.infer<typeof paymentAccountStatusSchema>;

/** The subset that describes an account whose KYC is in, but cannot charge. */
export type BlockedPaymentAccountStatus = Extract<
  PaymentAccountStatus,
  "inReview" | "actionRequired" | "rejected"
>;

/**
 * Wire projection of a payment provider account row, derived from its
 * lifecycle timestamps by `toPaymentProviderAccount`. Provider-neutral: the
 * account id keeps the row's `externalAccountId` name, and capabilities use
 * the platform-industry names (Adyen: receivePayments/payouts) rather than
 * Stripe's charges_enabled/payouts_enabled, which stay confined to the
 * webhook-snapshot adapter boundary.
 */
export const paymentProviderAccountSchema = z
  .object({
    externalAccountId: z.string(),
    detailsSubmitted: z.boolean(),
    capabilities: z.object({
      receivePayments: z.boolean(),
      payouts: z.boolean(),
    }),
    canAcceptPayments: z.boolean(),
  })
  .describe("PaymentProviderAccount");

type PaymentProviderAccount = z.infer<typeof paymentProviderAccountSchema>;

// NOTE (Max, 2026-07-22): Prisma rows carry Date timestamps; ark's Drizzle
// rows carry ISO strings. Both orderings agree — normalize through epoch millis.
type TimestampValue = Date | string;

/**
 * Structural row shape shared by the Prisma model and the ark Drizzle table,
 * so every runtime derives the wire account through this one module.
 */
export type PaymentProviderAccountTimestamps = {
  externalAccountId: string;
  detailsSubmittedAt: TimestampValue | null;
  chargesEnabledAt: TimestampValue | null;
  chargesDisabledAt: TimestampValue | null;
  payoutsEnabledAt: TimestampValue | null;
  payoutsDisabledAt: TimestampValue | null;
};

export function isCurrentlyEnabled({
  enabledAt,
  disabledAt,
}: {
  enabledAt: TimestampValue | null;
  disabledAt: TimestampValue | null;
}): boolean {
  if (!enabledAt) {
    return false;
  }
  if (!disabledAt) {
    return true;
  }
  return new Date(enabledAt).getTime() > new Date(disabledAt).getTime();
}

export function toPaymentProviderAccount(
  row: PaymentProviderAccountTimestamps,
): PaymentProviderAccount {
  const receivePayments = isCurrentlyEnabled({
    enabledAt: row.chargesEnabledAt,
    disabledAt: row.chargesDisabledAt,
  });
  return {
    // The account's id in the provider's own system (Stripe: "acct_…").
    externalAccountId: row.externalAccountId,
    // Onboarding paperwork (KYC) done. Monotonic — details never un-submit.
    detailsSubmitted: Boolean(row.detailsSubmittedAt),
    capabilities: {
      // Provider currently allows charging shoppers. Revocable at any time
      // (requirements past due, risk review), hence derived latest-wins.
      receivePayments,
      // Provider currently allows paying the merchant out. Independent of
      // receivePayments — a dead bank account kills payouts while sales continue.
      payouts: isCurrentlyEnabled({
        enabledAt: row.payoutsEnabledAt,
        disabledAt: row.payoutsDisabledAt,
      }),
    },
    // The gate every consumer asks about: is checkout usable right now.
    // Defined, not stored — a stored flag could contradict its inputs.
    canAcceptPayments: Boolean(row.detailsSubmittedAt) && receivePayments,
  };
}
